import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import StationCard from "@/components/StationCard";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import { MagnifyingGlass, Spinner, Radio as RadioIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Radios() {
  const [stations, setStations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tags, setTags] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favs, setFavs] = useState(new Set());

  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [country, setCountry] = useState("");
  const [tag, setTag] = useState("");
  const [language, setLanguage] = useState("");

  const { playStation } = usePlayer();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/radio/countries").then(r => setCountries(r.data || [])).catch(() => {});
    api.get("/radio/tags").then(r => setTags(r.data || [])).catch(() => {});
    api.get("/radio/languages").then(r => setLanguages(r.data || [])).catch(() => {});
  }, []);

  const loadFavs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/favorites/radios");
      setFavs(new Set(data.map(f => f.stationuuid)));
    } catch (_) { /* ignore */ }
  }, [user]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const fetchStations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (submittedSearch) params.name = submittedSearch;
      if (country) params.country = country;
      if (tag) params.tag = tag;
      if (language) params.language = language;
      const { data } = await api.get("/radio/stations", { params });
      setStations(data || []);
    } catch (_) {
      toast.error("Failed to load stations");
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, country, tag, language]);

  // Re-fetch when filters or submitted search change. Typing alone does not hit the API.
  useEffect(() => { fetchStations(); }, [fetchStations]);

  const onFav = async (station) => {
    if (!user) { toast.error("Please sign in to save favorites"); return; }
    try {
      if (favs.has(station.stationuuid)) {
        await api.delete(`/favorites/radios/${station.stationuuid}`);
        setFavs(prev => { const s = new Set(prev); s.delete(station.stationuuid); return s; });
        toast.success("Removed from favorites");
      } else {
        await api.post("/favorites/radios", {
          stationuuid: station.stationuuid,
          name: station.name,
          url: station.url_resolved || station.url,
          favicon: station.favicon || "",
          country: station.country || "",
          tags: station.tags || "",
        });
        setFavs(prev => new Set(prev).add(station.stationuuid));
        toast.success("Added to favorites");
      }
    } catch (_) { toast.error("Action failed"); }
  };

  const activeFilters = [country, tag, language].filter(Boolean).length;
  const clearFilters = () => { setCountry(""); setTag(""); setLanguage(""); setSearch(""); setSubmittedSearch(""); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12 pb-32 sm:pb-40">
      <div className="mb-5 sm:mb-10 border-b border-[#1F2924]/20 pb-4 sm:pb-6">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D35230] mb-2 sm:mb-3 flex items-center gap-2">
          <RadioIcon size={14} weight="fill" /> Atlas of Frequencies
        </div>
        <h1 className="font-serif-display text-3xl sm:text-5xl lg:text-6xl leading-tight">World Radio</h1>
        <p className="text-sm sm:text-base text-[#1F2924]/70 mt-2 sm:mt-3 max-w-2xl">
          40,000+ stations · search, browse by country, tune by genre.
        </p>
      </div>

      {/* Search */}
      <form
        onSubmit={(e) => { e.preventDefault(); setSubmittedSearch(search); }}
        className="flex items-center gap-2 mb-3 sm:mb-4"
      >
        <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus-within:border-[#D35230]">
          <MagnifyingGlass size={16} color="#4A5A50" />
          <input
            data-testid={TEST_IDS.radio.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stations…"
            className="flex-1 min-w-0 bg-transparent outline-none font-sans text-sm sm:text-base"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); setSubmittedSearch(""); }}
              aria-label="Clear search"
              className="w-6 h-6 text-[#4A5A50] hover:text-[#D35230] flex items-center justify-center shrink-0"
            ><X size={14} /></button>
          )}
        </div>
        <button
          type="submit"
          data-testid="radio-search-button"
          className="px-4 sm:px-5 min-h-[44px] py-2.5 border-2 border-[#1F2924] bg-[#1F2924] text-[#F4F1EA] hover:bg-[#D35230] hover:border-[#D35230] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] shrink-0"
        >
          Search
        </button>
      </form>

      {/* Filters — 3 columns on mobile, inline on desktop */}
      <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2 sm:gap-3 mb-5 sm:mb-8">
        <select
          data-testid={TEST_IDS.radio.countrySelect}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="col-span-3 sm:col-span-3 lg:col-span-5 min-w-0 px-2.5 sm:px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 font-mono text-[10px] sm:text-xs uppercase tracking-wider focus:border-[#D35230] outline-none truncate"
        >
          <option value="">All countries</option>
          {countries.map(c => (
            <option key={c.name} value={c.name}>{c.name} ({c.stationcount})</option>
          ))}
        </select>
        <select
          data-testid={TEST_IDS.radio.tagSelect}
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="col-span-3 sm:col-span-2 lg:col-span-4 min-w-0 px-2.5 sm:px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 font-mono text-[10px] sm:text-xs uppercase tracking-wider focus:border-[#D35230] outline-none truncate"
        >
          <option value="">All genres</option>
          {tags.slice(0, 60).map(t => (
            <option key={t.name} value={t.name}>{t.name}</option>
          ))}
        </select>
        <select
          data-testid={TEST_IDS.radio.langSelect}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="col-span-3 sm:col-span-1 lg:col-span-3 min-w-0 px-2.5 sm:px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 font-mono text-[10px] sm:text-xs uppercase tracking-wider focus:border-[#D35230] outline-none truncate"
        >
          <option value="">All languages</option>
          {languages.slice(0, 40).map(l => (
            <option key={l.name} value={l.name}>{l.name}</option>
          ))}
        </select>
      </div>

      {/* Active filter pills + clear */}
      {(activeFilters > 0 || search) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">Filters:</span>
          {search && <Pill label={`"${search}"`} onRemove={() => { setSearch(""); fetchStations(); }} />}
          {country && <Pill label={country} onRemove={() => setCountry("")} />}
          {tag && <Pill label={tag} onRemove={() => setTag("")} />}
          {language && <Pill label={language} onRemove={() => setLanguage("")} />}
          <button
            data-testid="radio-clear-filters"
            onClick={clearFilters}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] hover:underline ml-1"
          >Clear all</button>
        </div>
      )}

      {/* Results count */}
      {!loading && stations.length > 0 && (
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#4A5A50] mb-3 sm:mb-4">
          {stations.length} station{stations.length !== 1 ? "s" : ""}
        </div>
      )}

      {loading ? (
        <div data-testid="radio-loading" className="flex flex-col items-center gap-3 py-16 sm:py-24">
          <Spinner size={32} className="animate-spin" color="#D35230" />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230]">Tuning the dial…</div>
        </div>
      ) : stations.length === 0 ? (
        <div data-testid="radio-empty" className="text-center py-16 sm:py-24">
          <RadioIcon size={48} color="#D35230" weight="duotone" className="mx-auto mb-4" />
          <div className="font-serif-display text-2xl sm:text-3xl text-[#4A5A50] mb-2">No stations found</div>
          <div className="font-sans text-sm text-[#1F2924]/60 mb-4">Try clearing some filters, or pick a different country.</div>
          <button onClick={clearFilters} className="px-4 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em]">
            Reset
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 lg:gap-5">
          {stations.map(s => (
            <StationCard
              key={s.stationuuid}
              station={s}
              onPlay={playStation}
              onFav={onFav}
              isFav={favs.has(s.stationuuid)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#1F2924] text-[#F4F1EA] font-mono text-[10px] uppercase tracking-[0.15em]">
      <span className="truncate max-w-[8rem]">{label}</span>
      <button onClick={onRemove} aria-label={`Remove ${label}`} className="hover:text-[#D35230] shrink-0">
        <X size={10} weight="bold" />
      </button>
    </span>
  );
}
